import { useIntl } from "react-intl";
import { CcpaStatus } from "src/privacy-regulations/hooks/useCcpaManagement";
import { useApplicablePrivacyRegs } from "src/privacy-regulations/hooks/useApplicablePrivacyRegs";
import ChevronRight from "src/design-system/icons/ChevronRight.svg?react";
import { Button } from "react-aria-components";
import { sendAnalyticsInteractionEvent } from "src/analytics/sendAnalyticsEvent";
import { useFeature } from "../../../feature/useFeature";
import {
  getHelpLanguageCode,
  localeToLanguageCode,
} from "../../../utils/conversions/languageCode";
import { distanceSystemMessages } from "../../../utils/distanceSystem";
import useUser from "../../../utils/hooks/useUser";
import { supportedLanguageNames } from "../../../utils/language";
import { timeFormatMessages } from "../../TimeFormatScreen/TimeFormatScreen.messages";
import messages from "./UserPreferences.messages";

type Props = {
  onLanguageClick: () => void;
  onCurrencyClick: () => void;
  onDistanceClick: () => void;
  onTimeFormatClick: () => void;
  onDebugClick: () => void;
  onGDPRConsentClick: () => void;
  onUsPrivacyConsentClick: () => void;
  ccpaStatus: CcpaStatus;
};

export function UserPreferences(props: Props) {
  const intl = useIntl();
  const languageCode = localeToLanguageCode(intl.locale);
  const languageHelpCode = getHelpLanguageCode(languageCode);
  const { currencyCode, distanceSystem, prefers12Hour } = useUser();
  const isDebug = useFeature("Debug");

  const supportedTimeFormat = prefers12Hour
    ? intl.formatMessage(timeFormatMessages.timeFormat12H)
    : intl.formatMessage(timeFormatMessages.timeFormat24H);

  const privacyRegulations = useApplicablePrivacyRegs();
  const gdprApplies = privacyRegulations?.includes("gdpr");
  const usConsentApplies = privacyRegulations?.includes("usprivacy");

  const logClick = (gaActionValue: string) => {
    sendAnalyticsInteractionEvent({
      category: "MoreExtraDetail",
      action: `Click:${gaActionValue}`,
    });
  };

  return (
    <ul className="relative divide-y-1 divide-border border-y-1 border-border bg-surface">
      <li className="relative">
        <Button
          className="flex w-full cursor-pointer items-center justify-between px-5 py-3 no-underline data-focus-visible:ring data-pressed:bg-surface-active"
          onPress={props.onLanguageClick}
        >
          <span className="w-full text-left">
            {intl.formatMessage(messages.language)}
          </span>
          <span className="sr-only">:</span>

          <span className="block w-30 flex-grow overflow-hidden text-right text-nowrap overflow-ellipsis text-text-secondary">
            {supportedLanguageNames[languageCode]}
          </span>
          <ChevronRight
            aria-hidden="true"
            title={intl.formatMessage(messages.change)}
            className="h-5 w-5 flex-shrink-0"
          />
        </Button>
      </li>
      <li className="relative">
        <Button
          className="flex w-full cursor-pointer items-center justify-between px-5 py-3 no-underline data-focus-visible:ring data-pressed:bg-surface-active"
          onPress={props.onCurrencyClick}
        >
          <span className="w-full text-left">
            {intl.formatMessage(messages.currency)}
          </span>
          <span className="sr-only">:</span>

          <span className="block w-30 flex-grow overflow-hidden text-right text-nowrap overflow-ellipsis text-text-secondary">
            {currencyCode}
          </span>
          <ChevronRight
            aria-hidden="true"
            title={intl.formatMessage(messages.change)}
            className="h-5 w-5 flex-shrink-0"
          />
        </Button>
      </li>
      <li className="relative">
        <Button
          className="flex w-full cursor-pointer items-center justify-between px-5 py-3 no-underline data-focus-visible:ring data-pressed:bg-surface-active"
          onPress={props.onDistanceClick}
        >
          <span className="w-full text-left">
            {intl.formatMessage(messages.distance)}
          </span>
          <span className="sr-only">:</span>

          <span className="block w-30 flex-grow overflow-hidden text-right text-nowrap overflow-ellipsis text-text-secondary">
            {intl.formatMessage(distanceSystemMessages[distanceSystem])}
          </span>
          <ChevronRight
            aria-hidden="true"
            title={intl.formatMessage(messages.change)}
            className="h-5 w-5 flex-shrink-0"
          />
        </Button>
      </li>
      <li className="relative">
        <Button
          className="flex w-full cursor-pointer items-center justify-between px-5 py-3 no-underline data-focus-visible:ring data-pressed:bg-surface-active"
          onPress={props.onTimeFormatClick}
        >
          <span className="w-full text-left">
            {intl.formatMessage(messages.timeFormat)}
          </span>
          <span className="sr-only">:</span>

          <span
            className="block w-30 flex-grow overflow-hidden text-right text-nowrap overflow-ellipsis text-text-secondary"
            id="time-format-preference-title"
          >
            {supportedTimeFormat}
          </span>
          <ChevronRight
            aria-hidden="true"
            title={intl.formatMessage(messages.change)}
            className="h-5 w-5 flex-shrink-0"
          />
        </Button>
      </li>
      {isDebug && (
        <li className="relative">
          <Button
            className="flex w-full cursor-pointer items-center justify-between px-5 py-3 no-underline data-focus-visible:ring data-pressed:bg-surface-active"
            onPress={props.onDebugClick}
          >
            <span className="w-full text-left">
              {intl.formatMessage(messages.debugTitle)}
            </span>
            <span className="sr-only">:</span>
            <span className="block w-30 flex-grow overflow-hidden text-right text-nowrap overflow-ellipsis text-text-secondary">
              {intl.formatMessage(messages.debugDescription)}
            </span>
            <ChevronRight
              aria-hidden="true"
              title={intl.formatMessage(messages.change)}
              className="h-5 w-5 flex-shrink-0"
            />
          </Button>
        </li>
      )}
      {gdprApplies && (
        <li className="relative">
          <Button
            className="flex w-full cursor-pointer items-center justify-between px-5 py-3 no-underline data-focus-visible:ring data-pressed:bg-surface-active"
            onClick={props.onGDPRConsentClick}
          >
            <span className="w-full text-text-primary">
              {intl.formatMessage(messages.consent)}
            </span>
            <ChevronRight
              aria-hidden="true"
              title={intl.formatMessage(messages.change)}
              className="h-5 w-5 flex-shrink-0"
            />
          </Button>
        </li>
      )}
      {usConsentApplies && props.ccpaStatus === CcpaStatus.NOT_OPTED_OUT && (
        <li className="relative">
          <Button
            className="flex w-full cursor-pointer items-center justify-between px-5 py-3 no-underline data-focus-visible:ring data-pressed:bg-surface-active"
            onClick={props.onUsPrivacyConsentClick}
          >
            <span className="w-full text-text-primary">
              {intl.formatMessage(messages.doNotShare)}
            </span>
            <ChevronRight
              aria-hidden="true"
              title={intl.formatMessage(messages.change)}
              className="h-5 w-5 flex-shrink-0"
            />
          </Button>
        </li>
      )}
      <li className="relative">
        <a
          href={`https://help.rome2rio.com/${languageHelpCode}/support/tickets/new`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => logClick("Contact")}
          className="flex w-full cursor-pointer items-center justify-between px-5 py-3 no-underline data-focus-visible:ring data-pressed:bg-surface-active"
        >
          <span>{intl.formatMessage(messages.contact)}</span>
          <ChevronRight className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
        </a>
      </li>

      <li className="relative">
        <a
          href="https://www.rome2rio.com/about"
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => logClick("About")}
          className="flex w-full cursor-pointer items-center justify-between px-5 py-3 no-underline data-focus-visible:ring data-pressed:bg-surface-active"
        >
          <span>{intl.formatMessage(messages.about)}</span>
          <ChevronRight className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
        </a>
      </li>

      <li className="relative">
        <a
          href={`https://help.rome2rio.com/${languageHelpCode}/support/home`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => logClick("FAQ")}
          className="flex w-full cursor-pointer items-center justify-between px-5 py-3 no-underline data-focus-visible:ring data-pressed:bg-surface-active"
        >
          <span>{intl.formatMessage(messages.faq)}</span>
          <ChevronRight className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
        </a>
      </li>

      <li className="relative">
        <a
          href="https://www.rome2rio.com/travel-guides"
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => logClick("TravelGuides")}
          className="flex w-full cursor-pointer items-center justify-between px-5 py-3 no-underline data-focus-visible:ring data-pressed:bg-surface-active"
        >
          <span>{intl.formatMessage(messages.travelGuides)}</span>
          <ChevronRight className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
        </a>
      </li>

      <li className="relative">
        <a
          href="https://www.rome2rio.com/blog"
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => logClick("Blog")}
          className="flex w-full cursor-pointer items-center justify-between px-5 py-3 no-underline data-focus-visible:ring data-pressed:bg-surface-active"
        >
          <span>{intl.formatMessage(messages.blog)}</span>
          <ChevronRight className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
        </a>
      </li>

      <li className="relative">
        <a
          href="https://www.rome2rio.com/press"
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => logClick("Press")}
          className="flex w-full cursor-pointer items-center justify-between px-5 py-3 no-underline data-focus-visible:ring data-pressed:bg-surface-active"
        >
          <span>{intl.formatMessage(messages.press)}</span>
          <ChevronRight className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
        </a>
      </li>

      <li className="relative">
        <a
          href="https://www.rome2rio.com/careers"
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => logClick("Careers")}
          className="flex w-full cursor-pointer items-center justify-between px-5 py-3 no-underline data-focus-visible:ring data-pressed:bg-surface-active"
        >
          <span>{intl.formatMessage(messages.careers)}</span>
          <ChevronRight className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
        </a>
      </li>

      <li className="relative">
        <a
          href="https://www.rome2rio.com/facebook"
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => logClick("Facebook")}
          className="flex w-full cursor-pointer items-center justify-between px-5 py-3 no-underline data-focus-visible:ring data-pressed:bg-surface-active"
        >
          <span>{intl.formatMessage(messages.facebook)}</span>
          <ChevronRight className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
        </a>
      </li>

      <li className="relative">
        <a
          href="https://www.rome2rio.com/twitter"
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => logClick("Twitter")}
          className="flex w-full cursor-pointer items-center justify-between px-5 py-3 no-underline data-focus-visible:ring data-pressed:bg-surface-active"
        >
          <span>{intl.formatMessage(messages.twitter)}</span>
          <ChevronRight className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
        </a>
      </li>
    </ul>
  );
}
